import React, { useMemo, useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import {
  Button,
  CircularProgress,
  Grid
} from '@material-ui/core';
import { BackdropCircular } from 'components';
import { useFormik } from 'formik';
import { useStyles } from 'pages/quotes/styles';
import { setAutoQuoteItems } from 'redux/actions/automated_quotes';
import QuoteListingsItemRow from 'pages/quotes/QuoteItems/QuoteListings/QuoteListingsItemRow';
import QuoteListingsGroupSelect from 'pages/quotes/QuoteItems/QuoteListings/QuoteListingsGroupSelect';
import QuoteListingsFooter from 'pages/quotes/QuoteItems/QuoteListings/QuoteListingsFooter';
import { createValidationSchema } from 'pages/quotes/QuoteItems/QuoteListings/validation';
import { handleSelectDataSource } from 'pages/quotes/QuoteItems/QuoteListings//utils/handleSelectDataSource';
import { calculateTotals } from 'pages/quotes/QuoteItems/QuoteListings//utils/calculateTotals';


const AutomatedQuotesListings = (({ handleCancel }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {id} = useParams<{ [id: string]: string }>();
  const classes = useStyles();
  const ref = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [calculatedTotals, setCalculatedTotals] = useState({});

  const {
    quoteProductsByGroup,
    quoteGroupsLoading,
    quoteProductsLoading,
    quoteProducts,
    loadingQuoteProductSubscriptionOptions,
    subscriptionOptionsBySku,
  } = useSelector((state: IReduxState) => state.quote);

  const loading =
    quoteGroupsLoading ||
    quoteProductsLoading ||
    loadingQuoteProductSubscriptionOptions;

  const validationSchema = useMemo(() => createValidationSchema(
    quoteProductsByGroup,
    {},
    subscriptionOptionsBySku,
    handleSelectDataSource,
    false,
    {},
    calculatedTotals
  ), [
    quoteProductsByGroup,
    subscriptionOptionsBySku,
    calculatedTotals
  ]);

  const handleSave = async (values) => {
    const selectedProducts = values.items.map((item) => {
      const { sku, discountType, discount, qty, subscription_id, _id, fixedPrice, custom_subscription_price } = item;

      return {
        _id,
        sku,
        qty,
        price: fixedPrice,
        custom_subscription_price,
        product_option: {
          extension_attributes: {
            quotepro_discounts: {
              price_type: discountType,
              price: discount,
            },
            subscription_id
          },
        },
      };
    });

    dispatch(setAutoQuoteItems(selectedProducts));
    history.push(`/automated-quotes/${id}/quote-automation`);
  };

  const formik = useFormik({
    initialValues: {
      selectedGroup: '',
      items: [
        {
          selectedItemGroup: '',
          sku: '',
          fixedPrice: 0,
          qty: 1,
          subscription_id: '',
          discountType: '',
          subscriptionFrequency: '',
          additional_subscription_price: 0,
          savings: 0,
          discount: '',
          subscriptionDiscount: 'firstBill',
          custom_subscription_price: null,
        },
      ],
    },
    onSubmit: handleSave,
    validationSchema
  });

  const {
    values,
    handleSubmit,
    setValues,
    errors,
    touched
  } = formik;

  useEffect(() => {
    setIsLoading(true);
    setCalculatedTotals(calculateTotals(values.items, null, quoteProducts));
    setIsLoading(false);
  }, [values.items]);

  useEffect(() => {
    if (values.items.length) {
      ref.current?.scrollTo({
        top: ((values.items.length + 1) * 49),
        left: 0,
        behavior: 'smooth'
      });
    }
  }, [
    values.items.length,
    touched?.items?.[values?.items.length - 1],
    errors?.items?.[values?.items.length - 1]
  ]);

  const handleAddMoreItem = () => {
    const { items } = values;
    const updatedItems = [...items];
    const lastItemGroup = updatedItems[updatedItems.length - 1]?.selectedItemGroup || '';

    const newItem = {
      selectedItemGroup: lastItemGroup,
      sku: '',
      fixedPrice: 0,
      qty: 1,
      subscription_id: '',
      discountType: '',
      subscriptionFrequency: '',
      additional_subscription_price: 0,
      savings: 0,
      discount: '',
      subscriptionDiscount: 'firstBill',
      custom_subscription_price: null,
    };

    updatedItems.push(newItem);

    const updatedValues = {
      ...values,
      items: updatedItems,
      selectedGroup: lastItemGroup,
    };

    setValues(updatedValues);
  };

  return (
    <form autoComplete="off" onSubmit={handleSubmit}>
      {isLoading && (
        <Grid container spacing={2} justifyContent="center" className="p-4">
          <CircularProgress color="inherit" size={20} />
        </Grid>
      )}
      {!isLoading && (
        <>
          <div className={classes.quoteGroupDotted}>
            <QuoteListingsGroupSelect
              formik={formik}
            />
            <div className="mt-2" ref={ref}>
              {values?.items && values?.items?.map((item, index) => (
                <QuoteListingsItemRow
                  item={item}
                  index={index}
                  formik={formik}
                  handleSave={handleSave}
                  key={`selectedItemId-${index}-${item._id}`}
                />
              ))}
            </div>
          </div>
          <QuoteListingsFooter
            formik={formik}
            calculatedTotals={calculatedTotals}
            loading={loading}
            handleAddMoreItem={handleAddMoreItem}
          />
        </>
      )}
      <div className="flex flex-row align-center justify-between mt-4">
        <Button
          variant="outlined"
          color="primary"
          className="uppercase"
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={loading}
        >
          Save
        </Button>
      </div>
      <BackdropCircular loading={isLoading || loading} />
    </form>
  );
});

export default AutomatedQuotesListings;
